<template>
  <div
    data-component-group="threads-form"
    data-component="edit-deal"
    class="elevation-1"
  >
    <DealHero
      :thread="activeThread"
      :duplicates="duplicates"
      :on-change-action="onChangeAction"
      :on-update-thread="updateThread"
    />

    <AddCampaignForm
      :item="activeThread"
      confirm-trigger-label="Update campaign"
      :is-disabled="!canEditDetails"
      :hide-default-footer="!canEditThread"
      :is-loading="isLoading"
      :on-submit="onEditThread"
      :is-on="isOn"
    >
      <template v-slot:beforeForm>
        <layout-section-header
          title="Details"
          class="deal-details"
          :has-trigger="canEditThread"
          :trigger-label="!canEditDetails ? 'Edit details' : 'Close'"
          @toggle="toggleEditDetails"
        />
      </template>
    </AddCampaignForm>

    <MerchantForm
      v-show="canViewMerchant"
      :thread="activeThread"
      class="goto-merchant-section"
      :is-loading="isUpdatingMerchant && canEditMerchant"
      :is-disabled="!canEditMerchant"
      :on-submit="onUpdateMerchant"
    >
      <template v-slot:beforeForm>
        <layout-section-header
          title="Merchant"
          class="merchant-section"
          :trigger-label="!canEditMerchant ? 'Edit merchant' : 'Close'"
          @toggle="toggleMerchant"
        />
      </template>
      <template v-slot:afterForm>
        <Can
          v-if="hasMerchantDetails"
          action="see-external-merchant-deals"
          subject="thread"
        >
          <LatestDealsPanel :deals="merchantDetails.deals">
            <template v-slot:beforeForm>
              <layout-section-header
                title="Latest deals from this merchant"
                class="merchant-deals-section mt-4"
                trigger-label="See all deals"
                @toggle="seeAllMerchantDeals"
              />
            </template>
          </LatestDealsPanel>
        </Can>
      </template>
    </MerchantForm>

    <Can action="assign-labels" subject="thread">
      <LabelsForm
        :thread="activeThread"
        class="goto-labels-section"
        :is-loading="isUpdatingLabels"
        :is-disabled="!canEditLabels"
        :on-submit="onUpdateLabels"
      >
        <template v-slot:beforeForm>
          <layout-section-header
            title="Labels"
            :trigger-label="!canEditLabels ? 'Edit labels' : 'Close'"
            @toggle="toggleLabels"
          />
        </template>
      </LabelsForm>
    </Can>

    <UrlForm
      v-if="canViewDealUrl"
      :thread="activeThread"
      class="goto-deal-url-section"
      :is-loading="isPublishing"
      :is-disabled="!canEditDealUrl"
      :on-submit="publishWithUrl"
    >
      <template v-slot:beforeForm>
        <layout-section-header
          title="Campaign URL"
          :trigger-label="!canEditDealUrl ? 'Edit URL' : 'Close'"
          @toggle="toggleDealUrl"
        />
      </template>
    </UrlForm>

    <AddCommentForm
      class="goto-comments-section"
      :thread="activeThread"
      :is-loading="isAddingComment"
      can-attach-files
      :can-see-permissions="canSeeCommentPermissions"
      :on-deny="onDenyThread"
      :on-archive="onArchiveThread"
      :on-reopen="onReopenThread"
      :on-submit="onAddComment"
    >
      <template v-slot:beforeForm>
        <layout-section-header title="Deal Comments" :has-trigger="false" />
      </template>
      <template v-if="canViewComments" v-slot:afterForm>
        <CommentsPanel
          :comments="activeThread.comments"
          :is-loading="isGettingComments"
          :on-submit="filterComments"
        />
      </template>
    </AddCommentForm>

    <ActivityLogs
      v-if="activityLogs.length > 0"
      :rows="activityLogs"
      class="goto-activity-section"
    >
      <template v-slot:beforeForm>
        <layout-section-header title="Activity" :has-trigger="false" />
      </template>
    </ActivityLogs>
  </div>
</template>

<script>
  import DealHero from '../../panels/threads/DealHero';
  import AddCampaignForm from './AddCampaign';
  import LabelsForm from './Labels';
  import MerchantForm from './Merchant';
  import CommentsPanel from './Comments';
  import AddCommentForm from './AddComment';
  import LatestDealsPanel from '../../panels/threads/LatestDeals';
  import UrlForm from './DealUrl';
  import ActivityLogs from '../../lists/ActivityLogs';
  import {
    isNum,
    isArr,
    isObj,
    openUrlInNewWindow,
  } from '@/utils';

  // TODO: Translate component

  export default {
    components: {
      DealHero,
      AddCampaignForm,
      LabelsForm,
      MerchantForm,
      CommentsPanel,
      ActivityLogs,
      AddCommentForm,
      UrlForm,
      LatestDealsPanel,
    },
    props: {
      isOn: {
        type: Boolean,
        default: false,
      },
      isLoading: {
        type: Boolean,
        default: false,
      },
      isCleared: {
        type: Boolean,
        default: true,
      },
      onCancel: {
        type: Function,
        default: undefined,
      },
      onSubmit: {
        type: Function,
        required: true,
      },
      onUpdateThread: {
        type: Function,
        default: () => {},
      },
      onForceUpdateComments: {
        type: Function,
        default: () => {},
      },
    },
    data() {
      return {
        merchantDetails: null,
        canEditDetails: false,
        canEditLabels: false,
        canEditMerchant: false,
        canEditDealUrl: false,
        isUpdatingLabels: false,
        isUpdatingMerchant: false,
        isAddingComment: false,
        isGettingComments: false,
        isDenyingThread: false,
        isPublishing: false,
        isLoadingMerchantDetails: false,
      };
    },
    computed: {
      activeThread() {
        return this.$store.getters['threads/activeItem'];
      },
      hasThread() {
        const { activeThread } = this;
        return isObj(activeThread) && isNum(activeThread.id);
      },
      canModifyThread() {
        const { activeThread, activeUser } = this;

        return activeThread?.assignee?.id === activeUser?.id;
      },
      canEditThread() {
        const { activeThread } = this;

        return (
          isArr(activeThread.permissions) &&
          activeThread.permissions.indexOf('edit') > -1
        );
      },
      activeUser() {
        return this.$store.getters['auth/user'];
      },
      activityLogs() {
        const { log } = this.$store.getters['threads/activeItem'];
        return log.items;
      },
      duplicates() {
        const { duplicates } = this.$store.getters['threads/activeItem'];
        return duplicates;
      },
      lowerCasedName() {
        const { status = {} } = this.activeThread;
        const { name = '' } = status;

        return name.toLowerCase();
      },
      isPublished() {
        return this.lowerCasedName === 'published';
      },
      isDenied() {
        return this.lowerCasedName === 'declined';
      },
      canViewMerchant() {
        return (
          this.canModifyThread &&
          !this.isPublished &&
          !this.isDenied &&
          this.$ability.can('partnership-manager-store', 'thread') &&
          this.$ability.can('partnership-manager-update', 'thread')
        );
      },
      canViewComments() {
        const { activeThread } = this;
        const { permissions } = activeThread;

        return isArr(permissions) && permissions.indexOf('see-comments') > -1;
      },
      activeUserRole() {
        return this.$store.getters['auth/role'];
      },
      isBusinessContact() {
        return (
          this.activeUserRole.slug === 'marketing_agent' ||
          this.activeUserRole.slug === 'business_contact' ||
          this.activeUserRole.slug === 'business_admin'
        );
      },
      canViewDealUrl() {
        const { activeThread, isBusinessContact } = this;

        return (
          !isBusinessContact &&
          isArr(activeThread.permissions) &&
          activeThread.permissions.indexOf('publish-url') > -1
        );
      },
      hasMerchantDetails() {
        return isObj(this.merchantDetails) && isNum(this.merchantDetails.id);
      },
      hasNotes() {
        const { activeThread } = this;
        return isArr(activeThread.notes) && activeThread.notes.length > 0;
      },
      canSeeCommentPermissions() {
        const { activeThread } = this;

        return (
          isArr(activeThread.permissions) &&
          activeThread.permissions.indexOf('see-comment-permissions') > -1
        );
      },
      canSeeExternalMerchant() {
        return this.$ability.can('see-external-merchant-deals', 'thread');
      },
    },
    watch: {
      'activeThread.id'(newId) {
        if (newId) this.setupThread(this.activeThread);
      },
    },
    created() {
      if (this.hasThread) {
        this.setupThread(this.activeThread);
      }
    },
    mounted() {
      const { hash } = this.$route;

      if (hash === '#input_comment') {
        this.scrollToComments();
      }
    },
    methods: {
      setupThread(thread) {
        this.canEditDetails = false;
        this.canEditLabels = false;
        this.canEditMerchant = false;
        this.canEditDealUrl = false;

        this.getActivityLogs(thread);
        this.getComments(thread);

        if (this.canSeeExternalMerchant) {
          this.getMerchantDetails();
        }

        return this.getDuplicates(thread);
      },
      scrollToComments() {
        const target = document.querySelector('.comments-section');

        if (!isObj(target)) {
          return false;
        }

        target.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      },
      toggleEditDetails() {
        this.canEditDetails = !this.canEditDetails;
      },
      toggleLabels() {
        this.canEditLabels = !this.canEditLabels;
      },
      toggleMerchant() {
        this.canEditMerchant = !this.canEditMerchant;
      },
      getActivityLogs({ id }) {
        const query = { id, };

        return this.$store
          .dispatch('threads/getActivityLogs', query)
          .then((res) => {
            const hasErrors = this.$store.getters['alerts/error'].length > 0;

            if (hasErrors) {
              return false;
            }

            return res;
          });
      },
      getComments({ id }, filter = 'all') {
        const query = {
          id,
          filter,
        };

        this.isGettingComments = true;
        return this.$store
          .dispatch('threads/getComments', query)
          .then((comments) => {
            this.isGettingComments = false;
            return comments;
          })
          .catch((err) => {
            this.isGettingComments = false;
            return err;
          });
      },
      getDuplicates({ id }) {
        const query = { id };
        return this.$store.dispatch('threads/getDuplicates', query);
      },
      filterComments(filter) {
        this.getComments(this.activeThread, filter.action);
      },
      onUpdateLabels({ labels }) {
        const { activeThread } = this;

        const query = {
          id: activeThread.id,
          labels,
        };

        this.isUpdatingLabels = true;
        return this.$store
          .dispatch('threads/updateLabels', query)
          .then(() => {
            this.updateThread();
            return this.toggleLabels();
          })
          .catch(err => err)
          .finally(() => {
            this.isUpdatingLabels = false;
          });
      },
      async publishWithUrl(url) {
        const query = {
          id: this.activeThread.id,
          published_url: url,
        };

        return this.$store
          .dispatch('threads/publishThreadWithUrl', query)
          .then(() => {
            this.updateThread();
            return this.toggleDealUrl();
          }).catch(err => err);
      },
      generateCommentQuery({
        comment,
        permission,
        attachments,
        predefined_comment_id,
      }) {
        const query = {
          id: this.activeThread.id,
          comment: {
            comment,
            attachments,
            predefined_comment_id,
          },
        };

        if (isObj(permission)) {
          query.comment.permissions = permission.code;
        }

        return query;
      },
      onAddComment(comment) {
        this.isAddingComment = true;
        return this.$store
          .dispatch('threads/commentThread', this.generateCommentQuery(comment))
          .then((nextComment) => {
            this.updateThread();
            return nextComment;
          })
          .catch(err => err)
          .finally(() => {
            this.isAddingComment = false;
          });
      },
      onDenyThread(comment) {
        this.isAddingComment = true;
        return this.$store
          .dispatch('threads/denyThread', this.generateCommentQuery(comment))
          .then(() => {
            this.updateThread();
            this.onForceUpdateComments(); // only for single view
            return this.onCancel();
          })
          .catch(err => err)
          .finally(() => {
            this.isAddingComment = false;
          });
      },
      onArchiveThread(comment) {
        this.isAddingComment = true;
        return this.$store
          .dispatch('threads/archiveThread', this.generateCommentQuery(comment))
          .then(() => {
            this.updateThread();
            return this.onCancel();
          })
          .catch(err => err)
          .finally(() => {
            this.isAddingComment = false;
          });
      },
      onReopenThread(comment) {
        this.isAddingComment = true;
        return this.$store
          .dispatch('threads/reopenThread', this.generateCommentQuery(comment))
          .then(() => {
            this.updateThread();
            this.onForceUpdateComments(); // only for single view
            return this.onCancel();
          })
          .catch(err => err)
          .finally(() => {
            this.isAddingComment = false;
          });
      },
      onUpdateMerchant(merchant) {
        const { activeThread } = this;

        const query = {
          id: activeThread.id,
          merchant,
        };

        this.isUpdatingMerchant = true;
        return this.$store
          .dispatch('threads/addExternalMerchant', query)
          .then(() => {
            this.isUpdatingMerchant = false;
            this.getMerchantDetails();
            return this.toggleMerchant();
          })
          .catch(err => err)
          .finally(() => {
            this.isUpdatingMerchant = false;
          });
      },
      getMerchantDetails() {
        const query = {
          id: this.activeThread.id,
        };

        this.isLoadingMerchantDetails = true;
        return this.$store
          .dispatch('threads/getExternalMerchantDeals', query)
          .then((details) => {
            this.merchantDetails = details;
            return details;
          })
          .catch(() => {
            this.isLoadingMerchantDetails = false;
          });
      },
      onChangeAction({ action, id }) {
        const query = {
          id: this.activeThread.id,
        };

        if (action === 'user') {
          query.user = isNum(id) ? id : this.activeUser.id;
        } else if (action === 'partnership_manager' || action === 'editor') {
          query.queue = action;
        } else if (action === 'accept') {
          return this.acceptThread();
        } else if (action === 'deny') {
          return this.scrollToCommentSection();
        } else if (action === 'publish') {
          return this.publishThread();
        } else if (action === 'reopen') {
          return this.scrollToCommentSection();
        } else {
          query.user = id;
        }

        return this.$store
          .dispatch('threads/assignThread', query)
          .then((thread) => this.updateThread(thread))
          .catch(err => err);
      },
      updateThread(updatedThread = this.activeThread) {
        const hasAssignee = isObj(this.activeThread.assignee);
        const origin = hasAssignee
          ? 'user'
          : this.activeThread.queue === 'partnership_manager'
            ? 'keyAccount'
            : 'generalAll';

        return this.onUpdateThread(updatedThread, origin).then((res) => {
          this.setActiveThread(updatedThread);
          return res;
        });
      },
      toggleDealUrl() {
        this.canEditDealUrl = !this.canEditDealUrl;
      },
      scrollToCommentSection() {
        const target = document.querySelector('.goto-comments-section');

        if (!isObj(target)) {
          return false;
        }

        target.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      },
      acceptThread() {
        const query = {
          id: this.activeThread.id,
        };

        return this.$store.dispatch('threads/acceptThread', query).then((res) => {
          return res;
        }).catch(err => err);
      },
      publishThread() {
        const query = {
          id: this.activeThread.id,
        };

        return this.$store
          .dispatch('threads/publishThread', query)
          .then((res) => {
            this.onCancel();
            openUrlInNewWindow(window.open(), res.website);

            return res;
          })
          .catch(err => err);
      },
      seeAllMerchantDeals() {
        const { merchantDetails } = this;
        openUrlInNewWindow(window.open(), merchantDetails.url);
      },
      onEditThread(thread) {
        return this.onSubmit(thread);
      },
      setActiveThread(thread) {
        return this.$store.dispatch('threads/setActiveItem', thread);
      },
    },
  };
</script>

<style lang="scss">
@use 'sass:map';
@use '../../../assets/styles/theme' as *;

[data-component-group='threads-form'][data-component='edit-deal'] {
  > .component-header {
    position: relative;
  }

  &.deny-form-active {
    .deny-reason-box {
      opacity: 1;
      pointer-events: all;
      transition: all 0.15s ease-in-out;
    }

    > .component-body {
      opacity: 0;
      pointer-events: none;
      transition: all 0.15s ease-in-out;
    }
  }

  > .component-body {
    opacity: 1;
    pointer-events: all;
    transition: all 0.15s ease-in-out;
  }

  .deny-reason-box {
    opacity: 0;
    pointer-events: none;
    position: absolute;
    top: 49px;
    left: -31px;
    width: calc(100% + 62px);
    padding: 20px;
    z-index: 100;
    background-color: map.get($colors, shades, 'white');
    transition: all 0.15s ease-in-out;
  }

  .trigger.log-into-account,
  .trigger.allow-user,
  .trigger.deny-user {
    height: 28px;
    margin: 0;
    font-size: $text-size-sm;

    .v-icon {
      margin: 0 6px 0 0;
      font-size: $text-size-md;
    }

    &:before {
      background-color: map.get($colors, grey, base);
    }
  }

  .trigger.allow-user {
    margin: 0 10px 0 0;
  }

  .v-select__selection {
    color: map.get($colors, shades, 'white');
    background-color: map.get($colors, primary);
    border-radius: $border-radius;
    padding: 5px;
  }

  [data-component='file-upload'] {
    .v-btn {
      margin: 0;

      .v-icon {
        margin: 0 6px 0 0;
      }
    }
  }

  .deal-details {
    margin-bottom: 0;
  }

  [data-component='notes'] {
    [data-component='removables'] .v-list {
      padding: 0;

      .v-list-item {
        align-items: center;
      }
    }
  }
}
</style>
